/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { LinkRelationName, ListResource, ListResourceServiceConfig, StateResource } from '@common/tech-shared';
import { Resource } from '@ngxp/rest';

export interface CreateCommand {
  order: CommandOrder | any;
  body?: any | CommandBody;
  relationId?: string;
}

export interface CommandBody {
  item: any;
}

export interface Command extends CreateCommand {
  createdAt: Date;
  createdByName?: string;
  status?: CommandStatus;
  finishedAt: Date;
  errorMessage: string;
}

export interface CommandResource extends Command, Resource {}

export interface CommandListResource extends ListResource {}

export enum CommandStatus {
  REVOKED = 'REVOKED',
}

export enum CommandOrder {
  ANNEHMEN_WITH_ASSIGN = 'ANNEHMEN_WITH_ASSIGN',
  ASSIGN_USER = 'ASSIGN_USER',
  UNASSIGN_USER = 'UNASSIGN_USER',
  CREATE_KOMMENTAR = 'CREATE_KOMMENTAR',
  EDIT_KOMMENTAR = 'EDIT_KOMMENTAR',
  CREATE_WIEDERVORLAGE = 'CREATE_WIEDERVORLAGE',
  EDIT_WIEDERVORLAGE = 'EDIT_WIEDERVORLAGE',
  FORWARD_FAILED = 'FORWARD_FAILED',
  REDIRECT_VORGANG = 'REDIRECT_VORGANG',
  FORWARD_SUCCESSFULL = 'FORWARD_SUCCESSFULL',
  PROCESS_VORGANG = 'PROCESS_VORGANG',
  SEND_POSTFACH_NACHRICHT = 'SEND_POSTFACH_NACHRICHT',
  RECEIVE_POSTFACH_NACHRICHT = 'RECEIVE_POSTFACH_NACHRICHT',
  RESEND_POSTFACH_NACHRICHT = 'RESEND_POSTFACH_MAIL',
  WIEDERVORLAGE_ERLEDIGEN = 'WIEDERVORLAGE_ERLEDIGEN',
  WIEDERVORLAGE_WIEDEREROEFFNEN = 'WIEDERVORLAGE_WIEDEREROEFFNEN',
  VORGANG_ANNEHMEN = 'VORGANG_ANNEHMEN',
  VORGANG_VERWERFEN = 'VORGANG_VERWERFEN',
  VORGANG_ZURUECKHOLEN = 'VORGANG_ZURUECKHOLEN',
  ZURUECKHOLEN_WITH_UNASSIGN = 'ZURUECKHOLEN_WITH_UNASSIGN',
  VORGANG_BEARBEITEN = 'VORGANG_BEARBEITEN',
  VORGANG_BESCHEIDEN = 'VORGANG_BESCHEIDEN',
  FORWARD_VORGANG = 'FORWARD_VORGANG',
  VORGANG_ZURUECKSTELLEN = 'VORGANG_ZURUECKSTELLEN',
  VORGANG_ABSCHLIESSEN = 'VORGANG_ABSCHLIESSEN',
  VORGANG_WIEDEREROEFFNEN = 'VORGANG_WIEDEREROEFFNEN',
  VORGANG_ZUM_LOESCHEN_MARKIEREN = 'VORGANG_ZUM_LOESCHEN_MARKIEREN',
  MOVE_AND_DELETE = 'MOVE_AND_DELETE',
  VORGANG_LOESCHEN = 'VORGANG_LOESCHEN',
  VORGANG_VERAKTEN = 'VORGANG_VERAKTEN',
  LOESCH_ANFORDERUNG_ZURUECKNEHMEN = 'LOESCH_ANFORDERUNG_ZURUECKNEHMEN',
  CREATE_BESCHEID = 'CREATE_BESCHEID',
  SET_AKTENZEICHEN = 'SET_AKTENZEICHEN',
  DELETE_BESCHEID = 'DELETE_BESCHEID',
  UPDATE_BESCHEID = 'UPDATE_BESCHEID',
  CREATE_BESCHEID_DOCUMENT_FROM_FILE = 'CREATE_BESCHEID_DOCUMENT_FROM_FILE',
  CREATE_BESCHEID_DOCUMENT = 'CREATE_BESCHEID_DOCUMENT',
  SEND_BESCHEID = 'SEND_BESCHEID',
  CREATE_COLLABORATION_REQUEST = 'CREATE_COLLABORATION_REQUEST',
  WITHDRAW_COLLABORATION_REQUEST = 'WITHDRAW_COLLABORATION_REQUEST',
  CREATE_ZUARBEIT = 'CREATE_ZUARBEIT',
  SEND_VERAKTUNG_ABGABE = 'SEND_VERAKTUNG_ABGABE',
  CONFIRM_VERAKTUNG = 'CONFIRM_VERAKTUNG',
  EXPORT_VORGANG = 'EXPORT_VORGANG',
}

export interface CreateCommandActionProps {
  resource: Resource;
  linkRel: string;
  command: CreateCommand;
  snackBarMessage?: string;
  snackBarErrorMessage?: string;
}

export type CreateCommandActionPropsWithoutResource = Omit<CreateCommandActionProps, 'resource'>;

export interface PendingCommandMap {
  [relatedResourceUri: string]: StateResource<CommandResource>;
}

export interface SaveCommandProps {
  snackBarMessage: string;
}

export interface DeleteCommandProps {
  snackBarMessage: string;
}

export interface CommandListResourceServiceConfig<B> extends ListResourceServiceConfig<B> {
  listResourceListLinkRel: LinkRelationName;
  create?: { linkRel: LinkRelationName; order: CommandOrder };
}
